import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Linkedin, Mail, Phone, MapPin, MessageCircle, Download, FileText, ArrowRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { BRAND } from "../data/content";
import { trackEvent, EVENTS } from "@/utils/tracking";
import { getWhatsAppLink } from "@/utils/links";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import Img from "@/components/Img";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const { brochureUrl } = useSiteSettings();

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      trackEvent(EVENTS.NEWSLETTER_SUBSCRIBE, { location: "footer" });
      toast.success("Welcome to the list. Beautiful things ahead.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-[#2C2421] text-[#FAF6F1]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-20 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-x-16 md:gap-y-12">
          <div className="md:col-span-4">
            <Img
              imageKey="global.logo-light"
              defaultSrc={BRAND.logoLight || BRAND.logo}
              defaultAlt="Petals & Promises"
              className="h-12 md:h-14 w-auto mb-5"
              fit="contain"
            />
            <h3 className="font-display text-[26px] md:text-[30px] leading-[1.15] text-[#FAF6F1]">
              Where every celebration<br/>
              becomes a <span className="italic text-[#F4DCD8]">beautiful promise</span>.
            </h3>
            <p className="mt-5 text-[14px] text-[#FAF6F1] opacity-80 max-w-md leading-[1.7]">
              {BRAND.description}
            </p>
            <div className="mt-6 space-y-3 text-[13.5px] text-[#FAF6F1] opacity-95">
              <a href={`mailto:${BRAND.email}`} className="flex items-center gap-3 hover:text-[#F4DCD8] transition" data-testid="footer-email" onClick={() => trackEvent(EVENTS.EMAIL_CLICK, { location: "footer" })}>
                <Mail size={15} className="text-[#C5A059]" /> {BRAND.email}
              </a>
              <a href={`tel:${BRAND.phoneTel}`} className="flex items-center gap-3 hover:text-[#F4DCD8] transition" data-testid="footer-phone" onClick={() => trackEvent(EVENTS.PHONE_CLICK, { location: "footer", value: BRAND.phoneTel })}>
                <Phone size={15} className="text-[#C5A059]" /> {BRAND.phone}
              </a>
              <a href={`tel:${BRAND.phoneAltTel}`} className="flex items-center gap-3 hover:text-[#F4DCD8] transition" data-testid="footer-phone-alt" onClick={() => trackEvent(EVENTS.PHONE_CLICK, { location: "footer-alt", value: BRAND.phoneAltTel })}>
                <Phone size={15} className="text-[#C5A059]" /> {BRAND.phoneAlt}
              </a>
              <div className="flex items-start gap-3 leading-relaxed">
                <MapPin size={15} className="text-[#C5A059] mt-1 shrink-0" />
                <p className="leading-[1.7]">
                  Sector 51, Gurgaon · Sector 74, Chandigarh ·<br className="hidden sm:block"/>{" "}
                  Indira Nagar, Bangalore
                </p>
              </div>
            </div>
            <div className="mt-6 flex items-center gap-3 flex-wrap">
              {[
                { href: BRAND.socials.instagram, Icon: Instagram, test: "footer-instagram", label: "Instagram" },
                { href: BRAND.socials.facebook, Icon: Facebook, test: "footer-facebook", label: "Facebook" },
                { href: BRAND.socials.linkedin, Icon: Linkedin, test: "footer-linkedin", label: "LinkedIn" },
              ].map(({ href, Icon, test, label }) => (
                <a key={test} href={href} target="_blank" rel="noreferrer noopener" data-testid={test} aria-label={label}
                   className="w-9 h-9 rounded-full border border-[#FAF6F1]/30 flex items-center justify-center hover:bg-[#B65980] hover:border-[#B65980] transition">
                  <Icon size={15} />
                </a>
              ))}
              <a href={getWhatsAppLink()} target="_blank" rel="noreferrer noopener" data-testid="footer-whatsapp" onClick={() => trackEvent(EVENTS.WHATSAPP_CLICK, { location: "footer" })}
                 className="inline-flex items-center gap-2 ml-1 px-4 h-9 rounded-full bg-[#8A9A7B] text-white hover:bg-[#6E7E60] transition text-[11px] tracking-[0.18em] uppercase">
                <MessageCircle size={13} /> WhatsApp
              </a>
            </div>
          </div>

          {/* 3 link columns */}
          <FooterColumn className="md:col-span-2" title="Explore" links={EXPLORE_LINKS} testid="footer-col-explore" />
          <FooterColumn className="md:col-span-3" title="Services" links={FOOTER_SERVICES} testid="footer-col-services" />
          <FooterColumn className="md:col-span-3" title="Celebrations" links={FOOTER_CELEBRATIONS} testid="footer-col-celebrations" />
        </div>

        {/* Brochure / Proposal / Newsletter band — compact */}
        <div className="mt-12 rounded-2xl border border-[rgba(250,246,241,0.14)] bg-[rgba(250,246,241,0.04)] p-7 md:p-8 grid grid-cols-1 lg:grid-cols-12 gap-7 items-center">
          <div className="lg:col-span-6">
            <p className="text-[11px] tracking-[0.24em] uppercase text-[#C5A059] font-semibold">For planners &amp; hosts</p>
            <h3 className="font-display text-3xl md:text-4xl mt-3 text-[#FAF6F1] leading-tight">
              Download Our <span className="italic text-[#F4DCD8]">Celebration Deck</span>
            </h3>
            <p className="mt-4 text-[15px] text-[#FAF6F1] opacity-85 max-w-xl leading-[1.8]">
              Explore our services, planning approach, event categories, and experience design process
              in one beautifully curated brochure.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href={brochureUrl || "#"}
                target={brochureUrl ? "_blank" : undefined}
                rel={brochureUrl ? "noreferrer noopener" : undefined}
                aria-disabled={!brochureUrl}
                data-testid="footer-download-brochure"
                onClick={(e) => {
                  if (!brochureUrl) { e.preventDefault(); return; }
                  trackEvent(EVENTS.BROCHURE_DOWNLOAD_CLICK, { location: "footer" });
                }}
                className={`btn-gold ${!brochureUrl ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <Download size={14} /> Download Brochure
              </a>
              <Link to="/contact" data-testid="footer-custom-proposal" onClick={() => trackEvent(EVENTS.PLAN_EVENT_CTA_CLICK, { location: "footer-proposal" })} className="btn-ghost !text-[#F4DCD8] !border-[#F4DCD8]/55 hover:!bg-[#F4DCD8] hover:!text-[#B65980]">
                <FileText size={14} /> Request Custom Proposal
              </Link>
            </div>
          </div>

          {/* NEWSLETTER */}
          <div className="lg:col-span-6">
            <div className="rounded-[20px] bg-[#FAF6F1] text-[#3B312D] p-7 md:p-8">
              <p className="text-[11px] tracking-[0.24em] uppercase text-[#B65980] font-semibold">Newsletter</p>
              <h4 className="font-display text-2xl md:text-[28px] mt-2 text-[#3B312D] leading-tight">
                Subscribe to our <span className="italic text-[#B65980]">studio letters</span>.
              </h4>
              <p className="mt-3 text-[14px] text-[#3B312D]/75 leading-relaxed">
                Thoughtful planning notes, seasonal décor directions, and quiet design inspiration —
                delivered once a month. No spam, ever.
              </p>
              <form onSubmit={subscribe} className="mt-5 flex flex-col sm:flex-row items-stretch sm:items-center gap-3" data-testid="footer-newsletter-form">
                <input
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  data-testid="footer-newsletter-input"
                  className="w-full sm:flex-1 min-w-0 bg-white border border-[rgba(59,49,45,0.15)] rounded-full px-5 py-3 text-[14px] text-[#3B312D] placeholder:text-[#3B312D]/45 focus:outline-none focus:border-[#B65980] focus:ring-2 focus:ring-[#B65980]/20"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="footer-newsletter-submit"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 h-12 rounded-full bg-[#B65980] text-[#FAF6F1] hover:bg-[#9A4969] transition disabled:opacity-50 text-[12px] tracking-[0.14em] uppercase font-semibold whitespace-nowrap"
                >
                  {loading ? "Subscribing…" : "Subscribe"} {!loading && <ArrowRight size={14} />}
                </button>
              </form>
            </div>
          </div>
        </div>

        <div className="hairline mt-10" />
        <div className="mt-5 flex flex-col md:flex-row justify-between gap-3 text-[12.5px] text-[#FAF6F1] opacity-70">
          <p>© {new Date().getFullYear()} Petals &amp; Promises. All rights reserved.</p>
          <p>
            Designed with love by{" "}
            <a
              href="https://branditify.com"
              target="_blank"
              rel="noreferrer noopener"
              data-testid="footer-branditify"
              className="text-[#F4DCD8] hover:text-[#C5A059] underline-offset-4 hover:underline transition"
            >
              Branditify
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}

const FooterColumn = ({ title, links, testid, className = "" }) => (
  <div data-testid={testid} className={className}>
    <p className="text-[11px] tracking-[0.24em] uppercase text-[#C5A059] mb-4 font-semibold">{title}</p>
    <ul className="space-y-2 text-[13px] text-[#FAF6F1] opacity-90 leading-snug">
      {links.map((l) => (
        <li key={l.path + l.name}>
          <Link to={l.path} className="hover:text-[#F4DCD8] transition">{l.name}</Link>
        </li>
      ))}
    </ul>
  </div>
);

const EXPLORE_LINKS = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
  { name: "Services", path: "/services" },
  { name: "Celebrations", path: "/celebrations" },
  { name: "Venues", path: "/venues" },
  { name: "Checklist", path: "/checklist" },
  { name: "Portfolio", path: "/portfolio" },
  { name: "Packages", path: "/packages" },
  { name: "Process", path: "/process" },
  { name: "Insights", path: "/blogs" },
  { name: "Contact", path: "/contact" },
];

const FOOTER_SERVICES = [
  { name: "Wedding Planning", path: "/services#wedding-planning" },
  { name: "Social Celebrations", path: "/services#social-celebrations" },
  { name: "Corporate Events", path: "/services#corporate-events" },
  { name: "Devotional Events", path: "/services#devotional-events" },
  { name: "Event Design & Decor", path: "/services#event-design-decor" },
  { name: "Catering & Hospitality", path: "/services#catering-hospitality" },
  { name: "Venue Discovery & Booking", path: "/services#venue-discovery" },
  { name: "Entertainment & Artist Management", path: "/services#entertainment-artists" },
  { name: "Guest Management", path: "/services#guest-management" },
  { name: "Photography & Content Coordination", path: "/services#photography-content" },
  { name: "Production & Technicals", path: "/services#production-technicals" },
];

const FOOTER_CELEBRATIONS = [
  { name: "Roka Ceremony", path: "/celebrations#cel-Roka-Ceremony" },
  { name: "Engagement", path: "/celebrations#cel-Engagement" },
  { name: "Mehendi", path: "/celebrations#cel-Mehendi" },
  { name: "Haldi", path: "/celebrations#cel-Haldi" },
  { name: "Sangeet", path: "/celebrations#cel-Sangeet" },
  { name: "Cocktail Night", path: "/celebrations#cel-Cocktail-Night" },
  { name: "Wedding Ceremony", path: "/celebrations#cel-Wedding-Ceremony" },
  { name: "Reception", path: "/celebrations#cel-Reception" },
  { name: "Destination Wedding", path: "/celebrations#cel-Destination-Wedding" },
  { name: "Birthday Celebration", path: "/celebrations#cel-Birthday-Celebration" },
  { name: "Mata Ki Chowki", path: "/celebrations#cel-Mata-Ki-Chowki" },
  { name: "Corporate Launch", path: "/celebrations#cel-Corporate-Launch" },
  { name: "View All Celebrations", path: "/celebrations" },
];
